import React from "react";
import { isIOS, isAndroid } from "react-device-detect";
import {
    getTranslate,
    Translate,
    TranslateFunction,
} from "react-localize-redux";
import {connect} from "react-redux";
import {
    Button,
    Dropdown,
    Menu,
    Modal,
    Progress,
    Tooltip,
} from "antd";
import Nullable from "@data-force/app/interfaces/nullable";
import {
    CheckOutlined,
    ClockCircleOutlined,
    CloseOutlined,
    CopyOutlined,
    DownOutlined,
    ExclamationCircleOutlined,
    HourglassOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import dayjs, {Dayjs} from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactMarkdown from "react-markdown";
import {
    ClaimJobPhase,
    CompleteJobPhase,
    JobEntity,
    PhaseConfig,
    PhaseConfigNormalized,
} from "../../interfaces/store/actions/payload/job";
import {
    WorkflowDefinition,
    WorkflowDefinitionPhases,
} from "../../interfaces/store/actions/payload/workflowDefinition";
import {
    ClaimJobPhaseAction,
    CompleteJobPhaseAction,
    GetJobPhaseWorkflowDefinitionsAction,
    ShowConfirmClaimDialogAction,
} from "../../interfaces/store/actions/job";
import {
    formatString,
    getFormattedTime,
    getJobIconType,
    getOpeningUrl,
} from "../../utils/common";
import {
    TaskPhaseStats,
} from "../../interfaces/store/actions/payload/task";
import {
    JobNotification, JobTag, JobType, ProgressBarColors,
} from "../../enums/enums";
import StoreState from "../../interfaces/store/storeState";
import {
    claimJobPhase,
    completeJobPhase,
    getJobPhaseWorkflowDefinitions,
    showConfirmClaimDialog,
} from "../../store/actions/job";
import "./JobItem.scss";
import {Editors} from "../../interfaces/common";
import MenuClickParam from "../../interfaces/MenuClickParam";
import {ReadyTasksIcon} from "../../assets/Icons";

const {confirm, info}: typeof Modal = Modal;

type StatsData = {
    completedTasks: number
    rejectedTasks: number
    numOfTasks: number
};

dayjs.extend(duration);
dayjs.extend(relativeTime);

interface ProgressBarData {
    progressValue: number
    markAsRejected: boolean
}

interface OwnProps {
    data: JobEntity
    hideBtnDropdown: boolean
}

interface StateProps {
    editors: Editors
    phaseConfigList: PhaseConfigNormalized
    workflowDefinitionList: WorkflowDefinition[]
    translate: TranslateFunction
    isMobile: boolean
    activeJobType: string
    playStoreUrl: string
    appStoreUrl: string
}

interface DispatchProps {
    claimJobPhase(payload: ClaimJobPhase): ClaimJobPhaseAction

    completeJobPhase(payload: CompleteJobPhase): CompleteJobPhaseAction

    showConfirmClaimDialog(payload: JobEntity): ShowConfirmClaimDialogAction

    getJobPhaseWorkflowDefinitions(): GetJobPhaseWorkflowDefinitionsAction
}

type Props = OwnProps & StateProps & DispatchProps;

export class JobItem extends React.Component<Props> {
    private static readonly translationPath: string = "components.JobItem.JobItem";

    private static readonly translationConfirm: string = "components.JobItem.JobItem.confirmDialog";

    private static readonly translateMobileWarning: string = "components.JobItem.JobItem.mobileWarningDialog";

    private static readonly translateRecordingPhaseDialog: string = "components.JobItem.JobItem.recordingPhaseDialog";

    private static readonly translationPathJobActions: string = "enums.JobActions";

    private static readonly translateClaimLimit: string = "components.JobItem.JobItem.claimLimitNoticeDialog";

    private percentageFormat: string = this.props.translate(`${JobItem.translationPath}.percentageFormat.text`) as string;

    private titleFormat: string = this.props.translate(`${JobItem.translationPath}.titleFormat.text`) as string;

    /**
     * Based on event type we create job notification type
     * @param {string} event
     * @return {string}
     */
    static getJobNotification(event: string): string {
        let type: string = JobNotification.REOPEN;
        if (event === JobNotification.COMPLETE) {
            type = JobNotification.COMPLETE;
        } else if (event === JobNotification.REJECT) {
            type = JobNotification.REJECT;
        }
        return type;
    }

    constructor(props: Props) {
        super(props);
        this.claimJobPhaseHandler = this.claimJobPhaseHandler.bind(this);

        this.getAvailableClonesInfo = this.getAvailableClonesInfo.bind(this);

        this.getProgressBarData = this.getProgressBarData.bind(this);

        this.getPercentageFormat = this.getPercentageFormat.bind(this);

        this.getRemainingTime = this.getRemainingTime.bind(this);

        this.showClaimLimitNotice = this.showClaimLimitNotice.bind(this);

        this.detailsBtnHandler = this.detailsBtnHandler.bind(this);
    }

    /**
     * Fire complete job phase action with necessary arguments
     * @param {string} event
     * @param {string} notificationType
     */
    onOkHandler(event: string, notificationType: string): void {
        const {
            name: phase, projectId, jobId, title,
        }: JobEntity = this.props.data;
        this.props.completeJobPhase({
            projectId,
            jobId,
            phase,
            event,
            notificationType,
            phaseName: title,
        });
    }

    /**
     * Generates menu for current task from available actions provided
     *
     * @param {Nullable<string[]>} availableEvents
     */
    getMoreActionsMenu(availableEvents: Nullable<string[]>): React.ReactElement<Menu> {
        return (
            <Menu>
                {
                    availableEvents?.map((event: string): React.ReactNode => {
                        let eventKey: string = event;
                        let reopenPhaseName: string = "";
                        // Reject that reopens has phase name separated by space
                        const phaseNameIndex: number = event.indexOf(" ");
                        if (phaseNameIndex !== -1) {
                            eventKey = event.slice(0, phaseNameIndex);
                            reopenPhaseName = event.slice(phaseNameIndex);
                        }

                        let eventIndex: number = 1;
                        if (!reopenPhaseName) {
                            // Parallel phases will have .PhaseName in event name, we don't need to use multiple localizations for each one
                            eventIndex = 0;
                        }

                        eventKey = eventKey.split(".")[eventIndex];

                        return (
                            <Menu.Item
                                onClick={this.actionSelectedHandler.bind(this, event)}
                                key={event}
                            >
                                <Translate id={`${JobItem.translationPathJobActions}.${eventKey}.text`}/>
                                {reopenPhaseName}
                            </Menu.Item>
                        );
                    })
                }
            </Menu>
        );
    }

    /**
     * Returns available actions for current job
     *
     * @return {string[]}
     */
    getAvailableActions(): string[] {
        let availableEvents: string[] = [];
        const {
            name: phaseName,
            projectType,
            taskCount,
            completedTaskCount,
            phaseStats,
            workflowDefinitionId,
        }: JobEntity = this.props.data;
        if (phaseStats) {
            const {READY = 0, IN_PROGRESS = 0, REOPENED = 0}: TaskPhaseStats = phaseStats;
            const {phaseConfigList, workflowDefinitionList, getJobPhaseWorkflowDefinitions: getWorkflowDefinitions}: Props = this.props;
            const enableBulkCompletePhaseAction: boolean = phaseConfigList[`${projectType}-${phaseName}`]?.enableBulkCompletePhaseAction;

            if (enableBulkCompletePhaseAction && completedTaskCount !== taskCount && (READY || IN_PROGRESS || REOPENED)) {
                const workflowDefinition: Nullable<WorkflowDefinition> = workflowDefinitionList.find(
                    (currentWFD: WorkflowDefinition): boolean => workflowDefinitionId === currentWFD.id,
                );
                if (workflowDefinition) {
                    const phase: Nullable<WorkflowDefinitionPhases> = workflowDefinition.phases.find(
                        (currentPhase: WorkflowDefinitionPhases): boolean => currentPhase.name === phaseName,
                    );
                    if (phase) {
                        availableEvents = this.getPhaseActions(phase);
                    }
                } else if (!workflowDefinitionList.length) {
                    getWorkflowDefinitions();
                }
            }
        }

        return availableEvents;
    }

    /**
     * Returns due date in 'time x left' form or overdue text
     * @param {number} date
     * @return {React.ReactNode}
     */
    getRemainingTime(date: number): React.ReactNode {
        const {translate}: Props = this.props;
        const dateFormat: string = translate(`${JobItem.translationPath}.dateFormat.text`) as string;
        // everything less than 35h is considered as a 'day left' by dayjs, confirmed by the pdm
        const warningHours: number = 35;
        const now = dayjs(new Date());
        const dueDate: Nullable<Dayjs> = dayjs(date).hour(23).minute(59).second(59);
        const hoursLeft: number = dayjs.duration(dueDate.diff(now)).asHours();

        let overDueInfo: string = translate(`${JobItem.translationPath}.overdueText.text`) as string;
        if (dueDate.isAfter(now)) {
            overDueInfo = formatString(dateFormat, dueDate.fromNow(true));
        }
        return <span className={warningHours > hoursLeft ? "df-task-warning" : ""}>{overDueInfo}</span>;
    }

    /**
     * Returns available clones info for open jobs based ond job stats data
     *
     * @return {React.ReactNode}
     */
    getAvailableClonesInfo(): React.ReactNode {
        const {data, translate}: Props = this.props;
        const {
            cloneCount = 0,
            cloneLimit = 0,
            userCloneLimit = 0,
            userCloneCount = 0,
        }: JobEntity = data;

        const leftoverClones: number = cloneLimit - cloneCount;
        let availableClonesCount: number;

        if (userCloneCount) {
            // userCloneCount represent existence of at least one cloned job
            if (userCloneLimit <= leftoverClones) {
                availableClonesCount = userCloneLimit - userCloneCount;
            } else if (userCloneLimit === userCloneCount) {
                availableClonesCount = 0;
            } else {
                availableClonesCount = leftoverClones;
            }
        } else {
            availableClonesCount = leftoverClones > cloneCount ? userCloneLimit : leftoverClones;
        }

        return (
            <div className="df-job-stat">
                <Tooltip title={translate(`${JobItem.translationPath}.availableToClaim.text`)}>
                    <CopyOutlined/>
                    <span>{availableClonesCount}</span>
                </Tooltip>
            </div>
        );
    }

    /**
     * Creates list of available actions, for given phase
     *
     * @param {Nullable<WorkflowDefinitionPhases>} phase
     * @return {string[]}
     */
    getPhaseActions(phase: Nullable<WorkflowDefinitionPhases>): string[] {
        let phaseActions: string[] = [];
        if (phase) {
            const parallelPhases: boolean = !!phase.parent;
            if (parallelPhases) {
                const availableEvents: Nullable<string[]> = phase.availableEvents?.filter(
                    (event: string): boolean => event.includes(phase.name)
                );
                phaseActions = availableEvents || phaseActions;
            } else {
                phaseActions = phase.availableEvents || phaseActions;
            }
        }

        return phaseActions;
    }

    /**
     * Collect progress value and rejected status for progress bars
     * @return {ProgressBarData}
     */
    getProgressBarData(): ProgressBarData {
        const {data: {phaseStats = {}, taskCount = 0, completedTaskCount = 0}} = this.props;
        const {REJECTED = 0, SKIPPED = 0}: TaskPhaseStats = phaseStats;

        const totalTasks: number = taskCount - SKIPPED;
        const progressValue: number = totalTasks ? Math.round((completedTaskCount / totalTasks) * 100) : totalTasks;
        const markAsRejected: boolean = !!(totalTasks && REJECTED === totalTasks);

        return {
            progressValue,
            markAsRejected,
        };
    }

    /**
     * Returns progress bar with data and styling
     * @return React.ReactNode
     */
    getProgressBar(): React.ReactNode {
        const {progressValue, markAsRejected}: ProgressBarData = this.getProgressBarData();
        return (
            <Progress
                strokeWidth={6}
                percent={progressValue}
                format={this.getPercentageFormat}
                strokeColor={this.getProgressColor(progressValue, markAsRejected)}
            />
        );
    }

    /**
     * Format percentage text that goes inside of circle progress bars
     * @return {React.ReactNode}
     */
    getPercentageFormat(percent: number | undefined): React.ReactNode {
        return (
            <span>{formatString(this.percentageFormat, (percent as number).toString())}</span>
        );
    }

    /**
     * Based on completion percentage, we color progress bar in appropriate color
     * @param {number} tasksCompletionPercentage
     * @param {boolean} markAsRejected
     * @return {string} strokeColor
     */
    getProgressColor(tasksCompletionPercentage: number, markAsRejected: boolean): string {
        let strokeColor: string = ProgressBarColors.IN_PROGRESS;
        if (tasksCompletionPercentage === 100) {
            if (markAsRejected) {
                strokeColor = ProgressBarColors.REJECTED;
            } else {
                strokeColor = ProgressBarColors.COMPLETED;
            }
        }

        return strokeColor;
    }

    /**
     * Sanitize phaseStats data when there is faulty server side statsData
     * @param {JobEntity} data
     * @return {StatsData}
     */
    getStatsData(data: JobEntity): StatsData {
        const {taskCount = 0, phaseStats}: JobEntity = data;
        let completedTasks: number = 0;
        let rejectedTasks: number = 0;
        let numOfTasks: number = taskCount;
        if (phaseStats) {
            const {
                phaseStats: {COMPLETED = 0, REJECTED = 0, SKIPPED = 0},
            }: JobEntity = data;
            completedTasks = COMPLETED;
            rejectedTasks = REJECTED;
            numOfTasks -= SKIPPED;
        }

        return {completedTasks, rejectedTasks, numOfTasks};
    }

    /**
     * Details button handler, shows info dialog regarding dfc jobs
     * @param {React.MouseEvent} e
     */
    detailsBtnHandler(e: React.MouseEvent):void {
        e.stopPropagation();
        const {translateRecordingPhaseDialog}: typeof JobItem = JobItem;
        const {translate, appStoreUrl, playStoreUrl}: Props = this.props;

        let okBtnTxt: string = translate(`${translateRecordingPhaseDialog}.okBtnTxt.text`) as string;
        let content: string = translate(`${translateRecordingPhaseDialog}.content.text`) as string;

        if (isIOS) {
            okBtnTxt = translate(`${translateRecordingPhaseDialog}.iosBtnTxt.text`) as string;
            content = translate(`${translateRecordingPhaseDialog}.iosContent.text`) as string;
        }
        if (isAndroid) {
            okBtnTxt = translate(`${translateRecordingPhaseDialog}.androidBtnTxt.text`) as string;
            content = translate(`${translateRecordingPhaseDialog}.iosContent.text`) as string;
        }

        info({
            className: "df-recording-phase-modal",
            title: translate(`${translateRecordingPhaseDialog}.title.text`),
            content,
            centered: true,
            maskClosable: true,
            closable: true,
            okText: okBtnTxt,
            onOk: ():void => {
                if (isIOS) {
                    setTimeout(window.open, 100, appStoreUrl);
                }
                if (isAndroid) {
                    setTimeout(window.open, 100, playStoreUrl);
                }
            },
            width: 572,
        });
    }

    /**
     * Based on type of selected action show a confirm dialog
     * @param {string} action
     * @param {MenuClickParam} menuInfo
     */
    actionSelectedHandler(action: string, menuInfo: MenuClickParam): void {
        menuInfo.domEvent.stopPropagation();
        this.showConfirmation(action);
    }

    /**
     * Based on event type show confirmation dialog with appropriate text, pass event type to 'onOk' handler
     * @param {string} event
     */
    showConfirmation(event: string): void {
        const {translationConfirm}: typeof JobItem = JobItem;
        const {translate}: Props = this.props;
        const notificationType: string = JobItem.getJobNotification(event);
        confirm({
            className: "df-complete-confirm",
            title: translate(`${translationConfirm}.${notificationType}_title.text`),
            content: translate(`${translationConfirm}.content.text`),
            okText: translate(`${translationConfirm}.${notificationType}_btn.text`),
            cancelText: translate(`${translationConfirm}.cancelBtn.text`),
            centered: true,
            onOk: this.onOkHandler.bind(this, event, notificationType),
        });
    }

    /**
     * Based on displaying a popup notification when a user wants to claim more than the limit set
     */
    showClaimLimitNotice(): void {
        const {translateClaimLimit}: typeof JobItem = JobItem;
        const {translate, data: {name, title}}: Props = this.props;
        const formattedTitle: string = formatString(this.titleFormat, name, title);

        confirm({
            className: "df-claim-limit",
            title: translate(`${translateClaimLimit}.title.text`),
            content: (
                <>
                    {translate(`${translateClaimLimit}.contentPart1.text`)}
                    <span>{formattedTitle}</span>
                    {translate(`${translateClaimLimit}.contentPart2.text`)}
                </>),
            icon: <ExclamationCircleOutlined aria-hidden/>,
            centered: true,
            okCancel: false,
        });
    }

    /**
     * Actions container renderer function
     *
     * @param {JobEntity} data
     * @return {React.ReactNode}
     */
    actionsRenderer(data: JobEntity): React.ReactNode {
        let claimAvailable: boolean = true;
        const {
            started,
            assignee,
            open,
            taskCount,
            completedTaskCount,
            name,
        }: JobEntity = data;
        if (open) {
            const {
                cloneCount = 0,
                cloneLimit = 0,
                userCloneLimit = 0,
                stopped,
                userCloneCount = 0,
            }: JobEntity = data;

            claimAvailable = !stopped && (cloneLimit > cloneCount ? !!(userCloneLimit - userCloneCount) : false);
        }

        const completed: boolean = completedTaskCount === taskCount;

        return started && (!assignee || open) && !completed
            ? this.nonAssignedActionsRenderer(claimAvailable, name) : this.assignedActionsRenderer();
    }

    /**
     * Handles creation of 'claim' or 'details' button for dfc jobs.
     *
     * @param {boolean} claimAvailable
     * @param {string} phaseName
     * @return {Nullable<React.ReactNode>}
     */
    nonAssignedActionsRenderer(claimAvailable: boolean, phaseName: string): Nullable<React.ReactNode> {
        let btn: Nullable<React.ReactNode>;
        if (claimAvailable) {
            btn = phaseName === "Recording" ? (
                <Button
                    key="details"
                    type="primary"
                    onClick={this.detailsBtnHandler}
                >
                    <Translate id={`${JobItem.translationPath}.details.text`}/>
                </Button>
            ) : (
                <Button
                    key="claim"
                    type="primary"
                    onClick={this.claimJobPhaseHandler}
                >
                    <Translate id={`${JobItem.translationPath}.claim.text`}/>
                </Button>
            );
        }

        return btn;
    }

    /**
     * Creates actions visible when user is assigned to provided task.
     *
     * @return {React.ReactNode}
     */
    assignedActionsRenderer(): React.ReactNode {
        const {
            editors, phaseConfigList, data, hideBtnDropdown,
        }: Props = this.props;
        const availableEvents: Nullable<string[]> = this.getAvailableActions();
        const actionsMenu: React.ReactElement<Menu> = this.getMoreActionsMenu(availableEvents);
        const openJobUrl: string = getOpeningUrl(editors, phaseConfigList, data);
        const showDropdown: boolean = !hideBtnDropdown && !!availableEvents.length;
        const openBtnCls: string = classNames({
            "df-open-job": true,
            "df-open-with-dropdown": showDropdown,
        });

        return (
            <>
                <Button
                    className={openBtnCls}
                    key="open"
                    disabled={!openJobUrl}
                    onClick={this.openJobHandler.bind(this, openJobUrl)}
                >
                    <Translate id={`${JobItem.translationPath}.open.text`}/>
                </Button>
                { showDropdown && (
                    <Dropdown
                        className="df-more-actions"
                        overlay={actionsMenu}
                        trigger={["click"]}
                        placement="bottom"
                        align={{offset: [-25, 0]}}
                    >
                        <DownOutlined
                            key="more-actions"
                            onClick={this.stopEventPropagation}
                        />
                    </Dropdown>
                ) }
            </>
        );
    }

    /**
     * Show dialog for "Claim job phase confirmation"
     * @param {React.MouseEvent} e
     */
    claimJobPhaseHandler(e: React.MouseEvent): void {
        e.stopPropagation();
        const {data: {open, ongoingAvailableCount, ongoingCloneLimit}}: Props = this.props;
        if (ongoingCloneLimit && !ongoingAvailableCount && open) {
            this.showClaimLimitNotice();
        } else {
            this.props.showConfirmClaimDialog(this.props.data);
        }
    }

    /**
     * Open job button handler, if we are under 768px i.e. on mobile we show warning before opening the url
     * @param {string} url
     * @param {React.MouseEvent} e
     */
    openJobHandler(url: string, e: React.MouseEvent): void {
        e.stopPropagation();
        const {translate, isMobile} = this.props;
        if (isMobile) {
            const {translateMobileWarning}: typeof JobItem = JobItem;
            confirm({
                className: "df-warning-confirm",
                title: translate(`${translateMobileWarning}.title.text`),
                content: (
                    <>
                        <p>{translate(`${translateMobileWarning}.content.text`)}</p>
                        <p>{translate(`${translateMobileWarning}.question.text`)}</p>
                    </>
                ),
                okText: translate(`${translateMobileWarning}.continueBtn.text`),
                cancelText: translate(`${translateMobileWarning}.cancelBtn.text`),
                centered: true,
                onOk: ():void => {
                    setTimeout(window.open, 100, url);
                },
            });
        } else {
            window.open(url);
        }
    }

    /**
     * Prevents propagation of click event
     * @param {React.MouseEvent} e
     */
    stopEventPropagation(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    render() {
        const {
            data, translate, phaseConfigList, activeJobType,
        } = this.props;
        const {translationPath}: typeof JobItem = JobItem;
        const {
            dueDate,
            title,
            projectType,
            name,
            open,
            assignee,
            taskCount,
            description = "",
            estimatedTime,
            tags,
            timeLimit,
            phaseStats: {READY = 0},
        }: JobEntity = data;

        const {completedTasks, rejectedTasks, numOfTasks} = this.getStatsData(data);
        const currentPhaseConfig: PhaseConfig = phaseConfigList[`${projectType}-${name}`];
        const formattedTitle: string = formatString(this.titleFormat, name, title);

        return (
            <div className="df-job-item">
                <div className="df-job-upper">
                    {currentPhaseConfig && getJobIconType(currentPhaseConfig.editor)}
                    <div className="df-job-upper-container">
                        <Tooltip title={formattedTitle} placement="topLeft">
                            <strong>{formattedTitle}</strong>
                        </Tooltip>
                        {assignee && (
                            <div className="df-job-upper-info">
                                <div>
                                    {activeJobType === JobType.COMPLETED && tags.includes(JobTag.EXPIRED) && (
                                        <span className="df-task-warning">
                                            <Translate id={`${JobItem.translationPath}.expiredText.text`}/>
                                        </span>
                                    )}
                                    {dueDate && activeJobType !== JobType.COMPLETED && this.getRemainingTime(dueDate)}
                                </div>
                                <div className="df-progress-container">
                                    {this.getProgressBar()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="df-job-lower">
                    <ReactMarkdown linkTarget="_blank" className="df-job-desc">
                        {description}
                    </ReactMarkdown>
                    <div className="df-job-info-container">
                        <div className="df-job-stats-container">
                            {!!estimatedTime
                                && (
                                    <div className="df-job-stat">
                                        <Tooltip title={translate(`${translationPath}.estimatedTimeToComplete.text`)}>
                                            <ClockCircleOutlined
                                                aria-label={translate(`${translationPath}.estimatedTimeToComplete.text`) as string}
                                            />
                                            {getFormattedTime(estimatedTime, translate)}
                                        </Tooltip>
                                    </div>
                                )}
                            {!!timeLimit && activeJobType !== JobType.COMPLETED && (
                                <div className="df-job-stat">
                                    <Tooltip title={translate(`${translationPath}.expiryTimeToComplete.text`)}>
                                        <HourglassOutlined
                                            aria-label={translate(`${translationPath}.expiryTimeToComplete.text`) as string}
                                        />
                                        {getFormattedTime(timeLimit, translate)}
                                    </Tooltip>
                                </div>
                            )}

                            {!!taskCount && (
                                <div className="df-job-stat">
                                    {activeJobType !== JobType.COMPLETED && (
                                        <Tooltip title={translate(`${translationPath}.readyTasks.text`)}>
                                            <div className="df-job-ready-stat">
                                                <span role="img">
                                                    <ReadyTasksIcon
                                                        aria-label={translate(`${translationPath}.readyTasks.text`) as string}
                                                    />
                                                </span>
                                                <span>{READY}</span>
                                            </div>

                                        </Tooltip>
                                    )}
                                    <Tooltip title={translate(`${translationPath}.totalTasks.text`)}>
                                        <UnorderedListOutlined
                                            aria-label={translate(`${translationPath}.totalTasks.text`) as string}
                                        />
                                        <span>{numOfTasks}</span>
                                    </Tooltip>
                                </div>
                            )}

                            {assignee && !open && (
                                <div className="df-job-stat">
                                    <Tooltip title={translate(`${translationPath}.completedTasks.text`)}>
                                        <CheckOutlined aria-label={translate(`${translationPath}.completedTasks.text`) as string}/>
                                        <span className="df-job-rejected-stat">{completedTasks}</span>
                                    </Tooltip>
                                    <Tooltip title={translate(`${translationPath}.rejectedTasks.text`)}>
                                        <CloseOutlined aria-label={translate(`${translationPath}.rejectedTasks.text`) as string}/>
                                        <span>{rejectedTasks}</span>
                                    </Tooltip>
                                </div>
                            )}
                            {open && this.getAvailableClonesInfo()}
                        </div>
                        <div className="df-job-action">
                            {this.actionsRenderer(data)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        workflowDefinitionList: state.job.workflowDefinitions,
        phaseConfigList: state.job.phaseConfigList,
        editors: state.serviceProperties.editors,
        isMobile: state.mobile.isMobile,
        activeJobType: state.job.activeJobType,
        playStoreUrl: state.serviceProperties.playStoreUrl,
        appStoreUrl: state.serviceProperties.appStoreUrl,
    };
}

const mapDispatchToProps: DispatchProps = {
    claimJobPhase,
    completeJobPhase,
    showConfirmClaimDialog,
    getJobPhaseWorkflowDefinitions,
};

export default connect(mapStateToProps, mapDispatchToProps)(JobItem);
